// src/pages/ProductDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ShoppingBag,
  Star,
  Truck,
  Shield,
  RefreshCw,
  ChevronLeft,
  Send,
  AlertCircle,
} from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewLoading, setReviewLoading] = useState(false);

  // FETCH PRODUCT + REVIEWS (100% working)
  useEffect(() => {
    const fetchData = async () => {
      try {
        const productRes = await fetch(`/api/products/${id}/`);
        if (!productRes.ok) throw new Error();
        const productData = await productRes.json();
        setProduct(productData);

        if (productData.variants?.length > 0) {
          setSelectedColor(productData.variants[0].color);
        }
      } catch {
        setProduct(null);
      }

      // Fetch reviews (always fresh token)
      try {
        const token = localStorage.getItem("accessToken");
        const reviewsRes = await fetch(`/api/reviews/${id}/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(Array.isArray(reviewsData) ? reviewsData : []);
        }
      } catch {
        setReviews([]);
      }

      setLoading(false);
    };

    fetchData();
  }, [id]);

  // Current variant
  const currentVariant =
    product?.variants?.find(
      (v) => v.color === selectedColor && v.size === selectedSize
    ) ||
    product?.variants?.find((v) => v.color === selectedColor) ||
    product?.variants?.[0];

  const currentImage =
    currentVariant?.image_url ||
    "https://via.placeholder.com/800x900.png?text=No+Image";
  const currentStock = currentVariant?.stock || 0;
  const inStock = currentStock > 0;
  const lowStock = currentStock > 0 && currentStock <= 5;

  const colors = [...new Set(product?.variants?.map((v) => v.color) || [])];
  const sizes = [
    ...new Set(
      product?.variants
        ?.filter((v) => v.color === selectedColor)
        .map((v) => v.size) || []
    ),
  ];

  // Auto-select first size when color changes
  useEffect(() => {
    if (sizes.length > 0 && !selectedSize) {
      setSelectedSize(sizes[0]);
    }
  }, [selectedColor, sizes]);

  const handleAddToCart = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      alert("Please login to add items!");
      navigate("/login");
      return;
    }
    if (!selectedColor) return alert("Please select a color");
    if (sizes.length > 0 && !selectedSize) return alert("Please select a size");
    if (!inStock) return alert("Out of stock!");

    const cart = JSON.parse(localStorage.getItem("cart") || "[]");
    const key = `${product.id}-${selectedColor}-${selectedSize || "onesize"}`;
    const existing = cart.find((i) => i.key === key);

    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        key,
        id: product.id,
        name: product.name,
        price: product.offer_price || product.price,
        image: currentImage,
        color: selectedColor,
        size: selectedSize || null,
        quantity: 1,
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));
    alert("Added to cart!");
  };

  const handleSubmitReview = async () => {
    if (!newReview.trim() || rating === 0) {
      alert("Please write a review and give a rating");
      return;
    }

    setReviewLoading(true);
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        alert("Please login to review");
        navigate("/login");
        return;
      }

      const res = await fetch(`/api/reviews/${id}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ text: newReview, rating }),
      });

      if (res.ok) {
        const data = await res.json();
        setReviews([data, ...reviews]);
        setNewReview("");
        setRating(0);
        alert("Thank you! Review posted");
      } else {
        const err = await res.json();
        alert(err.error || "Failed to post");
      }
    } catch {
      alert("Network error");
    } finally {
      setReviewLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <p className="text-xl text-gray-600">Loading product...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-20">
          <AlertCircle className="w-20 h-20 text-red-500 mb-6" />
          <h1 className="text-4xl font-medium text-gray-800 mb-4">
            Product Not Found
          </h1>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-4 bg-black text-white rounded-xl hover:bg-gray-800 transition"
          >
            Back to Shop
          </button>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-20 left-4 bg-white p-3 rounded-full shadow-lg z-50 hover:shadow-xl transition"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <div className="min-h-screen bg-gray-100 pt-24 pb-16 font-['Roboto'] text-gray-800">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-10">

          {/* LEFT – IMAGE SECTION */}
          <div className="space-y-6">
            <div className="lg:sticky lg:top-24 bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="relative h-[70vh] bg-gray-50 flex items-center justify-center">
                <img
                  src={currentImage}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) =>
                    (e.target.src =
                      "https://via.placeholder.com/800x900.png?text=No+Image")
                  }
                />

                {/* Stock Badges */}
                {!inStock && (
                  <div className="absolute top-6 left-6 bg-red-600 text-white px-6 py-3 rounded-full font-medium">
                    Out of Stock
                  </div>
                )}
                {lowStock && (
                  <div className="absolute top-6 left-6 bg-orange-500 text-white px-6 py-3 rounded-full font-medium">
                    Only {currentStock} left!
                  </div>
                )}
              </div>

              {/* Color Thumbnails */}
              {colors.length > 0 && (
                <div className="p-6">
                  <p className="text-gray-700 mb-4">
                    Color:{" "}
                    <span className="font-medium capitalize">{selectedColor}</span>
                  </p>
                  <div className="flex gap-4 overflow-x-auto pb-2">
                    {colors.map((color) => {
                      const variant = product.variants.find(
                        (v) => v.color === color
                      );
                      return (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(color)}
                          className={`flex-shrink-0 w-20 h-20 rounded-xl border-2 transition-all overflow-hidden ${
                            selectedColor === color
                              ? "border-black shadow-lg"
                              : "border-gray-300 hover:border-gray-500"
                          }`}
                        >
                          {variant?.image_url ? (
                            <img
                              src={variant.image_url}
                              alt={color}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                              {color}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT – INFO + REVIEWS */}
          <div className="space-y-8">

            {/* Breadcrumb */}
            <p className="text-sm text-gray-500">
              Home › Clothing › {product.category || "Tops"} › {product.name}
            </p>

            {/* Title */}
            <h1 className="text-4xl font-medium text-gray-900">{product.name}</h1>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((s) => (
                  <Star
                    key={s}
                    className={`w-5 h-5 ${
                      s <= 4 ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                    }`}
                  />
                ))}
              </div>
              <span className="text-lg text-gray-700">4.2</span>
              <span className="text-gray-600">• {reviews.length} reviews</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-medium text-gray-900">
                ₹{product.offer_price || product.price}
              </span>
              {product.offer_price && (
                <span className="text-2xl text-gray-500 line-through">
                  ₹{product.price}
                </span>
              )}
            </div>

            {/* Stock */}
            <p className="text-lg">
              Availability:{" "}
              <span
                className={inStock ? "text-green-600 font-medium" : "text-red-600 font-medium"}
              >
                {inStock ? `${currentStock} in stock` : "Out of stock"}
              </span>
            </p>

            {/* Colors */}
            {colors.length > 0 && (
              <div>
                <p className="text-gray-700 mb-3">
                  Color: <span className="font-medium capitalize">{selectedColor}</span>
                </p>
                <div className="flex flex-wrap gap-3">
                  {colors.map((color) => {
                    const variant = product.variants.find((v) => v.color === color);
                    return (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-6 py-3 rounded-xl border-2 text-sm font-medium transition-all flex items-center gap-2 ${
                          selectedColor === color
                            ? "border-black bg-black text-white"
                            : "border-gray-300 hover:border-gray-600"
                        }`}
                      >
                        {color}
                        {variant?.image_url && (
                          <div
                            className="w-5 h-5 rounded-full border border-gray-400"
                            style={{
                              backgroundImage: `url(${variant.image_url})`,
                              backgroundSize: "cover",
                              backgroundPosition: "center",
                            }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sizes */}
            {sizes.length > 0 && (
              <div>
                <p className="text-gray-700 mb-3">
                  Size: <span className="font-medium">{selectedSize}</span>
                </p>
                <div className="flex flex-wrap gap-4">
                  {sizes.map((size) => {
                    const stock =
                      product.variants.find(
                        (v) => v.color === selectedColor && v.size === size
                      )?.stock || 0;

                    return (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        disabled={stock === 0}
                        className={`w-16 h-16 rounded-xl border-2 text-lg font-medium transition-all ${
                          selectedSize === size
                            ? "border-black bg-black text-white"
                            : stock === 0
                            ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "border-gray-300 hover:border-gray-700"
                        }`}
                      >
                        {size}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Add to Cart */}
            <button
              onClick={handleAddToCart}
              disabled={!inStock}
              className={`w-full py-5 rounded-xl font-medium text-lg transition-all flex items-center justify-center gap-3 ${
                inStock
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-gray-400 text-gray-600 cursor-not-allowed"
              }`}
            >
              <ShoppingBag className="w-6 h-6" />
              {inStock ? "Add to Cart" : "Out of Stock"}
            </button>

            {/* Benefits */}
            <div className="grid grid-cols-3 gap-6 py-8 border-t border-gray-200 text-center">
              <div>
                <Truck className="w-10 h-10 mx-auto text-green-600 mb-2" />
                <p className="text-sm font-medium">Free Delivery</p>
              </div>
              <div>
                <RefreshCw className="w-10 h-10 mx-auto text-blue-600 mb-2" />
                <p className="text-sm font-medium">10 Days Return</p>
              </div>
              <div>
                <Shield className="w-10 h-10 mx-auto text-purple-600 mb-2" />
                <p className="text-sm font-medium">Secure Payment</p>
              </div>
            </div>

            {/* Product Details */}
            <div className="border-t pt-8 space-y-6 text-gray-700">
              <div>
                <h3 className="text-xl font-medium mb-3">Product Details</h3>
                <p className="text-base leading-relaxed">
                  {product.description ||
                    "Premium quality fabric with modern fit. Comfortable for all-day wear."}
                </p>
              </div>
            </div>

            {/* REVIEWS SECTION */}
            <div className="border-t pt-10 mt-10">
              <h3 className="text-2xl font-medium mb-8 text-gray-900">
                Customer Reviews
              </h3>

              {/* Write Review */}
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 mb-8">
                <textarea
                  value={newReview}
                  onChange={(e) => setNewReview(e.target.value)}
                  placeholder="Share your thoughts about this product..."
                  className="w-full h-32 p-4 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-black"
                />
                <div className="flex items-center justify-between mt-5">
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        onClick={() => setRating(star)}
                        className={`w-9 h-9 cursor-pointer transition-all ${
                          star <= rating
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300 hover:text-yellow-400"
                        }`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleSubmitReview}
                    disabled={reviewLoading || !newReview.trim() || rating === 0}
                    className="px-8 py-3 bg-black text-white rounded-xl font-medium hover:bg-gray-800 disabled:opacity-50 transition flex items-center gap-2"
                  >
                    <Send className="w-5 h-5" />
                    {reviewLoading ? "Posting..." : "Submit Review"}
                  </button>
                </div>
              </div>

              {/* Reviews List */}
              {reviews.length === 0 ? (
                <p className="text-center text-gray-500 py-12 text-lg">
                  No reviews yet. Be the first to review!
                </p>
              ) : (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div
                      key={review.id}
                      className="bg-white p-6 rounded-2xl shadow-sm border"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {review.user || "Anonymous"}
                          </p>
                          <div className="flex gap-1 mt-1">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <Star
                                key={s}
                                className={`w-5 h-5 ${
                                  s <= review.rating
                                    ? "fill-yellow-400 text-yellow-400"
                                    : "text-gray-300"
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">{review.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}